<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import MetaBusinessAgentConversationControlAPI from 'dashboard/api/saas/metaBusinessAgentConversationControl';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  controlVersion: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['updated']);
const { t } = useI18n();
const control = ref(null);
const isLoading = ref(false);
const isMutating = ref(false);
const releaseConfirmDialog = ref(null);
let loadRequestSequence = 0;
let settlingReloadTimer = null;
let pollingFailureCount = 0;
let isDisposed = false;
const SETTLING_RELOAD_INTERVAL_MS = 2000;
const MAX_POLLING_BACKOFF_MS = 30000;

const isVisible = computed(
  () =>
    control.value?.applicable === true &&
    control.value?.provider === 'meta_business_agent'
);
const state = computed(() => control.value?.state || 'unconfirmed');
const actions = computed(() => control.value?.actions || {});
const assumeAction = computed(() => actions.value.assume_with_reply || {});
const releaseAction = computed(() => actions.value.release || {});
const canAssumeWithReply = computed(
  () =>
    assumeAction.value.available === true &&
    assumeAction.value.behavior === 'send_message'
);
const canRelease = computed(() => releaseAction.value.available === true);
const isMetaResponderActive = computed(
  () => state.value === 'meta_responder_active'
);
const showReleaseAction = computed(
  () =>
    !isMetaResponderActive.value &&
    (canRelease.value || control.value?.owner === 'human')
);
const assumeWithReplyLabel = computed(() =>
  isMetaResponderActive.value
    ? t(
        'CONVERSATION_SIDEBAR.META_BUSINESS_AGENT.ASSUME_WITH_REPLY_META_RESPONDER'
      )
    : t('CONVERSATION_SIDEBAR.META_BUSINESS_AGENT.ASSUME_WITH_REPLY')
);
const isAwaitingConfirmation = computed(
  () => control.value?.awaiting_provider_confirmation === true
);
const stateCopy = computed(() => {
  const copies = {
    not_applicable: {
      label: t(
        'CONVERSATION_SIDEBAR.META_BUSINESS_AGENT.STATES.not_applicable.LABEL'
      ),
      description: t(
        'CONVERSATION_SIDEBAR.META_BUSINESS_AGENT.STATES.not_applicable.DESCRIPTION'
      ),
    },
    unconfirmed: {
      label: t(
        'CONVERSATION_SIDEBAR.META_BUSINESS_AGENT.STATES.unconfirmed.LABEL'
      ),
      description: t(
        'CONVERSATION_SIDEBAR.META_BUSINESS_AGENT.STATES.unconfirmed.DESCRIPTION'
      ),
    },
    meta_active: {
      label: t(
        'CONVERSATION_SIDEBAR.META_BUSINESS_AGENT.STATES.meta_active.LABEL'
      ),
      description: t(
        'CONVERSATION_SIDEBAR.META_BUSINESS_AGENT.STATES.meta_active.DESCRIPTION'
      ),
    },
    meta_responder_active: {
      label: t(
        'CONVERSATION_SIDEBAR.META_BUSINESS_AGENT.STATES.meta_responder_active.LABEL'
      ),
      description: t(
        'CONVERSATION_SIDEBAR.META_BUSINESS_AGENT.STATES.meta_responder_active.DESCRIPTION'
      ),
    },
    takeover_pending: {
      label: t(
        'CONVERSATION_SIDEBAR.META_BUSINESS_AGENT.STATES.takeover_pending.LABEL'
      ),
      description: t(
        'CONVERSATION_SIDEBAR.META_BUSINESS_AGENT.STATES.takeover_pending.DESCRIPTION'
      ),
    },
    human_active: {
      label: t(
        'CONVERSATION_SIDEBAR.META_BUSINESS_AGENT.STATES.human_active.LABEL'
      ),
      description: t(
        'CONVERSATION_SIDEBAR.META_BUSINESS_AGENT.STATES.human_active.DESCRIPTION'
      ),
    },
    release_requesting: {
      label: t(
        'CONVERSATION_SIDEBAR.META_BUSINESS_AGENT.STATES.release_requesting.LABEL'
      ),
      description: t(
        'CONVERSATION_SIDEBAR.META_BUSINESS_AGENT.STATES.release_requesting.DESCRIPTION'
      ),
    },
    release_pending: {
      label: t(
        'CONVERSATION_SIDEBAR.META_BUSINESS_AGENT.STATES.release_pending.LABEL'
      ),
      description: t(
        'CONVERSATION_SIDEBAR.META_BUSINESS_AGENT.STATES.release_pending.DESCRIPTION'
      ),
    },
    release_ambiguous: {
      label: t(
        'CONVERSATION_SIDEBAR.META_BUSINESS_AGENT.STATES.release_ambiguous.LABEL'
      ),
      description: t(
        'CONVERSATION_SIDEBAR.META_BUSINESS_AGENT.STATES.release_ambiguous.DESCRIPTION'
      ),
    },
    ownership_conflict: {
      label: t(
        'CONVERSATION_SIDEBAR.META_BUSINESS_AGENT.STATES.ownership_conflict.LABEL'
      ),
      description: t(
        'CONVERSATION_SIDEBAR.META_BUSINESS_AGENT.STATES.ownership_conflict.DESCRIPTION'
      ),
    },
  };
  return copies[state.value] || copies.unconfirmed;
});
const statusLabel = computed(() => stateCopy.value.label);
const statusDescription = computed(() => stateCopy.value.description);
const liveStatusText = computed(() =>
  [
    statusLabel.value,
    statusDescription.value,
    isAwaitingConfirmation.value
      ? t('CONVERSATION_SIDEBAR.META_BUSINESS_AGENT.AWAITING_PROVIDER')
      : null,
  ]
    .filter(Boolean)
    .join('. ')
);
const statusClasses = computed(() => {
  if (isMetaResponderActive.value) {
    return 'bg-n-violet-3 text-n-violet-11';
  }
  if (['meta_active'].includes(state.value)) {
    return 'bg-n-teal-3 text-n-teal-11';
  }
  if (['human_active'].includes(state.value)) {
    return 'bg-n-blue-3 text-n-blue-11';
  }
  if (['ownership_conflict', 'release_ambiguous'].includes(state.value)) {
    return 'bg-n-ruby-3 text-n-ruby-11';
  }
  return 'bg-n-amber-3 text-n-amber-11';
});
const firstReleaseBlocker = computed(() => releaseAction.value.blockers?.[0]);
const releaseBlockerText = computed(() => {
  if (isAwaitingConfirmation.value) return '';
  if (!firstReleaseBlocker.value) return '';
  const blockers = {
    thread_control_release_inactive: t(
      'CONVERSATION_SIDEBAR.META_BUSINESS_AGENT.BLOCKERS.thread_control_release_inactive'
    ),
    thread_control_not_owned: t(
      'CONVERSATION_SIDEBAR.META_BUSINESS_AGENT.BLOCKERS.thread_control_not_owned'
    ),
    thread_control_takeover_settling: t(
      'CONVERSATION_SIDEBAR.META_BUSINESS_AGENT.BLOCKERS.thread_control_takeover_settling'
    ),
    thread_control_ownership_unconfirmed: t(
      'CONVERSATION_SIDEBAR.META_BUSINESS_AGENT.BLOCKERS.thread_control_ownership_unconfirmed'
    ),
    meta_business_agent_live_operations_disabled: t(
      'CONVERSATION_SIDEBAR.META_BUSINESS_AGENT.BLOCKERS.meta_business_agent_live_operations_disabled'
    ),
  };
  return (
    blockers[firstReleaseBlocker.value.code] ||
    firstReleaseBlocker.value.message ||
    t('CONVERSATION_SIDEBAR.META_BUSINESS_AGENT.BLOCKED')
  );
});

function clearSettlingReload() {
  if (settlingReloadTimer === null) return;

  window.clearTimeout(settlingReloadTimer);
  settlingReloadTimer = null;
}

function controlNeedsPolling() {
  return (
    isAwaitingConfirmation.value ||
    firstReleaseBlocker.value?.code === 'thread_control_takeover_settling'
  );
}

function scheduleControlReload(delay = SETTLING_RELOAD_INTERVAL_MS) {
  if (isDisposed) return;

  clearSettlingReload();
  settlingReloadTimer = window.setTimeout(() => {
    settlingReloadTimer = null;
    loadControl(); // eslint-disable-line no-use-before-define
  }, delay);
}

function pollingBackoff() {
  return Math.min(
    SETTLING_RELOAD_INTERVAL_MS * 2 ** pollingFailureCount,
    MAX_POLLING_BACKOFF_MS
  );
}

const idempotencyKey = () =>
  window.crypto?.randomUUID?.() ||
  `meta-business-agent:thread-control:${props.conversationId}:${Date.now()}-${Math.random()
    .toString(16)
    .slice(2)}`;

async function loadControl() {
  if (isDisposed || !props.conversationId) return;

  clearSettlingReload();
  const wasPolling = controlNeedsPolling();
  const conversationId = props.conversationId;
  loadRequestSequence += 1;
  const requestSequence = loadRequestSequence;
  isLoading.value = true;
  try {
    const response =
      await MetaBusinessAgentConversationControlAPI.get(conversationId);
    if (
      requestSequence !== loadRequestSequence ||
      isDisposed ||
      String(conversationId) !== String(props.conversationId)
    ) {
      return;
    }
    control.value = response.data;
    pollingFailureCount = 0;
    clearSettlingReload();
    if (controlNeedsPolling()) scheduleControlReload();
  } catch (error) {
    if (requestSequence !== loadRequestSequence || isDisposed) return;
    if ([403, 404].includes(error?.response?.status)) {
      control.value = null;
      pollingFailureCount = 0;
      clearSettlingReload();
    } else {
      useAlert(t('CONVERSATION_SIDEBAR.META_BUSINESS_AGENT.LOAD_FAILED'));
      if (wasPolling && controlNeedsPolling()) {
        pollingFailureCount += 1;
        scheduleControlReload(pollingBackoff());
      }
    }
  } finally {
    if (requestSequence === loadRequestSequence) {
      isLoading.value = false;
    }
  }
}

function assumeWithReply() {
  emitter.emit(BUS_EVENTS.PREPARE_PUBLIC_REPLY, {
    conversationId: Number(props.conversationId),
  });
}

async function releaseToMeta() {
  const conversationId = props.conversationId;
  const confirmed = await releaseConfirmDialog.value?.showConfirmation();
  if (!confirmed || String(conversationId) !== String(props.conversationId)) {
    return;
  }

  isMutating.value = true;
  try {
    const response = await MetaBusinessAgentConversationControlAPI.release(
      conversationId,
      idempotencyKey()
    );
    if (String(conversationId) !== String(props.conversationId)) return;
    control.value = response.data?.control || control.value;
    useAlert(t('CONVERSATION_SIDEBAR.META_BUSINESS_AGENT.RELEASE_ACCEPTED'));
    emit('updated', control.value);
    await loadControl();
  } catch (error) {
    if (String(conversationId) !== String(props.conversationId)) return;
    const blocker = error?.response?.data?.blockers?.[0];
    useAlert(
      blocker?.message ||
        error?.response?.data?.message ||
        t('CONVERSATION_SIDEBAR.META_BUSINESS_AGENT.RELEASE_FAILED')
    );
    await loadControl();
  } finally {
    isMutating.value = false;
  }
}

watch(
  () => props.conversationId,
  () => {
    clearSettlingReload();
    control.value = null;
    loadControl();
  }
);
watch(
  () => props.controlVersion,
  (value, previousValue) => {
    if (value && value !== previousValue) loadControl();
  }
);
onMounted(loadControl);
onBeforeUnmount(() => {
  isDisposed = true;
  loadRequestSequence += 1;
  clearSettlingReload();
});
</script>

<template>
  <div>
    <section
      v-if="isVisible"
      data-test-id="meta-business-agent-control"
      class="mb-4 rounded-xl border border-n-weak bg-n-alpha-1 p-3"
    >
      <div class="flex items-start justify-between gap-2">
        <div class="flex min-w-0 items-center gap-2">
          <span
            class="flex size-7 shrink-0 items-center justify-center rounded-lg bg-n-teal-3 text-n-teal-11"
          >
            <Icon icon="i-lucide-sparkles" class="size-4" />
          </span>
          <div class="min-w-0">
            <p class="text-sm font-medium text-n-slate-12">
              {{ $t('CONVERSATION_SIDEBAR.META_BUSINESS_AGENT.TITLE') }}
            </p>
            <p class="text-xs text-n-slate-10">
              {{ $t('CONVERSATION_SIDEBAR.META_BUSINESS_AGENT.SUBTITLE') }}
            </p>
          </div>
        </div>
        <span
          class="shrink-0 rounded-full px-2 py-1 text-[11px] font-medium"
          :class="statusClasses"
        >
          {{ statusLabel }}
        </span>
      </div>

      <p class="mt-2 text-xs leading-5 text-n-slate-11">
        {{ statusDescription }}
      </p>

      <p class="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {{ liveStatusText }}
      </p>

      <p
        v-if="isAwaitingConfirmation"
        class="mt-2 flex items-center gap-1.5 text-xs text-n-amber-11"
      >
        <Icon
          icon="i-lucide-loader-circle"
          class="size-3 animate-spin"
          aria-hidden="true"
        />
        {{ $t('CONVERSATION_SIDEBAR.META_BUSINESS_AGENT.AWAITING_PROVIDER') }}
      </p>

      <div class="mt-3 flex flex-col gap-2">
        <NextButton
          v-if="canAssumeWithReply"
          outline
          slate
          sm
          start
          class="w-full"
          icon="i-lucide-user-round-check"
          :label="assumeWithReplyLabel"
          @click="assumeWithReply"
        />
        <NextButton
          v-if="showReleaseAction"
          faded
          teal
          sm
          start
          class="w-full"
          icon="i-lucide-sparkles"
          :disabled="!canRelease || isMutating || isLoading"
          :is-loading="isMutating"
          :label="
            $t('CONVERSATION_SIDEBAR.META_BUSINESS_AGENT.RELEASE_TO_META')
          "
          @click="releaseToMeta"
        />
        <p
          v-if="!isMetaResponderActive && !canRelease && releaseBlockerText"
          class="text-xs text-n-slate-10"
        >
          {{ releaseBlockerText }}
        </p>
      </div>

      <woot-confirm-modal
        ref="releaseConfirmDialog"
        :title="
          $t('CONVERSATION_SIDEBAR.META_BUSINESS_AGENT.CONFIRM_RELEASE.TITLE')
        "
        :description="
          $t(
            'CONVERSATION_SIDEBAR.META_BUSINESS_AGENT.CONFIRM_RELEASE.DESCRIPTION'
          )
        "
        :confirm-label="
          $t('CONVERSATION_SIDEBAR.META_BUSINESS_AGENT.CONFIRM_RELEASE.CONFIRM')
        "
        :cancel-label="
          $t('CONVERSATION_SIDEBAR.META_BUSINESS_AGENT.CONFIRM_RELEASE.CANCEL')
        "
      />
    </section>
  </div>
</template>
